import { DetailCardItemData } from '@/common/components/DetailCard/DetailCard.types';
import Button from '@/common/components/base/button/Button';
import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { ONLY_NUMBERS_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import MainModal from '@/common/components/base/modal/Modal';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import useModal from '@/common/hooks/useModal';
import useInquiryTokenQueryOptions from '@/common/queries/getInquiryTokenQuery/getInquiryToken.options';
import { GetInquiryTokenResponse } from '@/common/queries/getInquiryTokenQuery/getInquiryToken.types';
import convertTimeStampToDate from '@/common/utils/convertTimeStampToDate';
import convertTimestampToShamsi from '@/common/utils/convertTimestampToShamsi';
import { NATIONAL_CODE_RULE } from '@/common/utils/rules/nationalCode.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import InfoIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/info.svg?react';
import { TInquiryForm } from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/PersonalInquiry.type';
import ShowInquiryData from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/components/ShowInquiryData';
import UserInputMismatch from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/components/UserInputMismatch';
import { usePersonalInquiryQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/personalInquiryQuery/personalInquiry.options';

import { PersonalInquiry } from '@/modules/adjusterReportModules/IncidentParticipants/queries/personalInquiryQuery/personalInquiry.types';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Box } from 'styled-system/jsx';

type params = {
  open: boolean;
  outSideForm: typeof useFormContext;
  closeModal: () => void;
};

const formattedData = (data: PersonalInquiry): DetailCardItemData[] => [
  {
    type: 'default',
    keyName: 'کد ملی:',
    value: data?.identity_info?.national_code,
    hasBullet: true,
  },
  {
    type: 'default',
    keyName: 'نام:',
    value: data?.identity_info?.first_name,
    hasBullet: true,
  },
  {
    type: 'default',
    keyName: 'نام خانوادگی:',
    value: data?.identity_info?.last_name,
    hasBullet: true,
  },
  {
    type: 'default',
    keyName: 'نام پدر:',
    value: data?.identity_info.father_name,
    hasBullet: true,
  },
  {
    type: 'default',
    keyName: 'جنسیت:',
    value: data?.identity_info?.gender === 'MALE' ? 'آقا' : 'خانم',
    hasBullet: true,
  },
  {
    type: 'default',
    keyName: 'شماره شناسنامه:',
    value: data?.identity_info?.identification_number,
    hasBullet: true,
  },
  {
    type: 'default',
    keyName: 'محل تولد:',
    value: data?.identity_info?.birth_place,
    hasBullet: true,
  },
  {
    type: 'default',
    keyName: 'تاریخ تولد:',
    value: !!data?.identity_info?.birth_date_timestamp
      ? convertTimeStampToDate(
          data?.identity_info?.birth_date_timestamp,
          {},
          false,
        )
      : null,
    hasBullet: true,
  },
];

const PersonalInquiryForm: React.FC<params> = ({
  open,
  outSideForm,
  closeModal,
}) => {
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const outSideMethods = outSideForm();
  const {
    closeModal: closeShowStatsModal,
    open: openShow,
    openModal: openShowModal,
  } = useModal();
  const [inquiryCardDataState, setInquiryCardDataState] =
    useState<DetailCardItemData[]>();
  const methods = useForm<TInquiryForm>();
  const {
    mutate: mutateInquiry,
    isSuccess,
    isPending: inquiryLoading,
    data: resData,
  } = useMutation(usePersonalInquiryQueryOptions());

  const { mutate: getInquiryToken, isPending: tokenLoading } =
    useInquiryTokenQueryOptions();

  const submit = (data: TInquiryForm) => {
    const formattedBirthDate = data.birth_date?.replace(/\//g, '-');
    getInquiryToken(undefined, {
      onSuccess: (tokenData: GetInquiryTokenResponse) => {
        mutateInquiry(
          {
            param: {
              ...data,
              birth_date: formattedBirthDate,
              token: tokenData.access,
            },
          },
          {
            onSuccess: (res) => {
              if (res?.data) {
                setInquiryCardDataState(() =>
                  formattedData(res?.data?.response),
                );
              }
              openShowModal();
              methods?.reset({ birth_date: '', national_code: '' });
              closeModal();
            },
            onError: () => {
              setOpenErrorModal(true);
            },
          },
        );
      },
      onError: () => {},
    });
  };
  return (
    <>
      <UserInputMismatch
        open={openErrorModal}
        setOpenModal={() => setOpenErrorModal(false)}
        status={400}
      />

      <ShowInquiryData
        inquiryCardDataState={inquiryCardDataState}
        isSuccess={isSuccess}
        showPersonalDataModal={openShow}
        onCloseModal={() => {
          closeShowStatsModal();
        }}
        setData={() => {
          const fetchedIdentityInquiryData =
            resData?.data.response?.identity_info;
          if (fetchedIdentityInquiryData) {
            outSideMethods.reset({
              ...outSideMethods.getValues(),
              personal_information: {
                ...outSideMethods.getValues()?.personal_information,
                birthdate: fetchedIdentityInquiryData?.birth_date_timestamp
                  ? convertTimestampToShamsi(
                      fetchedIdentityInquiryData?.birth_date_timestamp,
                    )
                  : undefined,
                gender:
                  fetchedIdentityInquiryData?.gender === 'MALE'
                    ? 'male'
                    : 'feMale',
                first_name: fetchedIdentityInquiryData?.first_name,
                last_name: fetchedIdentityInquiryData?.last_name,
                driver_iranian: 'true',
                identity_number:
                  fetchedIdentityInquiryData?.identification_number?.toString(),
                identity_issue_location:
                  fetchedIdentityInquiryData?.birth_place,
                national_code: fetchedIdentityInquiryData?.national_code,
                father_name: fetchedIdentityInquiryData?.father_name,
              },
            });
          }
          closeShowStatsModal();
        }}
      />

      <MainModal open={open} closeModal={() => closeModal()}>
        <TitleDescription>استعلام اطلاعات پایه </TitleDescription>
        <FormProvider {...methods}>
          <Box mt={32} display="flex" flexDirection="column" gap={16}>
            <TextInput
              name="national_code"
              label="کد ملی"
              noCharInput
              rules={{
                required: requiredText,
                ...ONLY_NUMBERS_RULE,
                ...NATIONAL_CODE_RULE,
              }}
              max={10}
            />
            <DateInput
              label="تاریخ تولد"
              buttonText="ثبت"
              modalLabel="تاریخ تولد"
              name="birth_date"
              rules={{ required: requiredText }}
            />
            <Box display="flex" alignItems="start">
              <Box w={24} pt={4}>
                <InfoIcon />
              </Box>
              <Typography color="black-3">
                با استعلام کد ملی و تاریخ تولد می‌توانید، اطلاعات مربوط به نام،
                نام خانوادگی، نام پدر، جنسیت، شماره شناسنامه و محل ‌تولد راننده
                را مشاهده کنید.
              </Typography>
            </Box>
            <Box
              display="flex"
              alignItems="start"
              justifyContent="space-between"
              gap={8}
            >
              <Button onClick={() => closeModal()} variant="outlined" w={'50%'}>
                بازگشت
              </Button>
              <LoadingButton
                onClick={methods.handleSubmit(submit)}
                loading={inquiryLoading || tokenLoading}
                w={'50%'}
              >
                تایید
              </LoadingButton>
            </Box>
          </Box>
        </FormProvider>
      </MainModal>
    </>
  );
};
export default PersonalInquiryForm;
